import { ProjectDot } from '@/components/ProjectDot';
import { Info } from 'lucide-react';
import { Kbd, Tooltip } from '@/components/ui';
import { useProjectMap } from '@/data/hooks';
import { cn } from '@/lib/cn';
import { formatDuration } from '@/lib/time';
import { switchKey } from '@/timer/switchTargets';
import type { Switcher } from './useSwitchTargets';

/**
 * The tasks in hand, one click from the clock (TW-30, Track only). For juggling tasks that run side by side, such as
 * several agents at work: one clock, exact time, and a switch costs a click or a number key. It parks the run on the
 * clock and resumes the one picked (TW-32). Phones get one row of chips, without the keys.
 */
export function SwitchTo({ switcher, compact = false }: { switcher: Switcher; compact?: boolean }) {
  const projects = useProjectMap();
  const { targets, tasks, switchTo } = switcher;
  if (!targets.length) return null;
  const rows = targets.map((t, i) => {
    const s = t.session;
    const project = s.projectId ? projects.get(s.projectId) : undefined;
    // A task is shown under its current name; the session keeps the name it had when it was logged.
    const name = (s.taskId && tasks.get(s.taskId)?.name) || s.goalText.trim() || project?.name || 'No goal';
    return { t, i, project, name };
  });

  return (
    // On a phone the label shares the chips' line: every row above the ring pushes the controls towards the tab bar.
    // On a desktop it stands apart from the goal above, which is the run on the clock rather than one waiting.
    <section aria-label="Parked timers" className={cn('flex', compact ? 'items-center gap-2 min-w-0' : 'flex-col gap-1 mt-6')}>
      {/* How to use it is a hover or a tap away rather than a line of its own: it is read once, and the list stays quiet. */}
      <Tooltip tap side={compact ? 'top' : 'right'} label={compact ? 'Tap one to switch to it.' : 'Click one, or press its number, to switch to it.'}>
        <button type="button" className={cn('self-start inline-flex items-center gap-[5px] p-0 border-0 bg-transparent text-eyebrow text-ink-2 cursor-help hover:text-ink', compact && 'shrink-0')}>
          {compact ? 'Parked' : 'Parked timers'}<Info size={12} aria-hidden="true" className="text-ink-3" />
        </button>
      </Tooltip>
      <ul className={cn('m-0 p-0 list-none', compact ? 'flex gap-2 overflow-x-auto min-w-0' : 'flex flex-col')}>
        {rows.map(({ t, i, project, name }) => (
          <li key={t.key} className={cn(compact && 'shrink-0')}>
            <button type="button" onClick={() => void switchTo(t)} aria-label={`Resume ${name}`}
              className={cn('flex items-center gap-2 border-0 bg-transparent cursor-pointer text-left text-ink hover:bg-raised',
                compact ? 'max-w-[200px] h-8 px-3 rounded-pill border border-border bg-card text-[12.5px] font-semibold' : 'w-full -mx-2 px-2 py-[6px] rounded-input text-[13px] font-semibold')}>
              {!compact && <Kbd>{switchKey(i)}</Kbd>}
              {project && <ProjectDot project={project} />}
              <span className="truncate min-w-0 flex-1">{name}</span>
              {/* Its time so far: what the clock picks up from. */}
              {!compact && <span className="font-mono text-[11.5px] text-ink-2 shrink-0">{formatDuration(t.session.durationSec, { zero: '<1m' })}</span>}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
